import { useState } from 'react'
import type { OrderDetailResponse } from '@fitting-room/contracts'
import { Download } from 'lucide-react'
import { ApiRequestError, fetchInvoicePdf } from '../../../lib/api'
import { useStaffToken } from '../../../lib/staffToken'

/*
 * The invoice, out of the console. "Download PDF" and "Email invoice" live in
 * the order's Order progress card now (CMP-9 § 1), and Email invoice is the
 * invoice page (`InvoicePage`); this keeps the download both of them use.
 */

/**
 * The PDF, handed to the browser as a file rather than linked: the console's
 * sign-in travels in a header, which a plain link can't carry. On the order
 * page and on the invoice page alike; the invoice page passes the `blob` its
 * preview already holds, so the file saved is the one on the screen, and
 * fetches only while that isn't loaded.
 */
export function DownloadPdfButton({
  detail,
  blob: ready,
  className = 'st-btn-ghost',
}: {
  readonly detail: OrderDetailResponse
  readonly blob?: Blob | null
  readonly className?: string
}) {
  const { token } = useStaffToken()
  const [downloading, setDownloading] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const download = async () => {
    if (!token && !ready) return
    setDownloading(true)
    setProblem(null)
    try {
      const blob = ready ?? (await fetchInvoicePdf(token ?? '', detail.order.id))
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${detail.order.orderNo}-invoice.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      // Revoked a beat later: Safari starts the download after the click returns.
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
    } catch (error) {
      setProblem(error instanceof ApiRequestError ? error.message : 'Couldn’t make the PDF. Try again in a moment.')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <>
      <button type="button" className={className} onClick={() => void download()} disabled={downloading}>
        <Download size={14} aria-hidden />
        {downloading ? 'Making the PDF…' : 'Download PDF'}
      </button>
      {problem ? (
        <p className="st-alert w-full text-sm" role="alert">
          {problem}
        </p>
      ) : null}
    </>
  )
}
